import { FieldArrayWithId, UseFormReturn } from 'react-hook-form';

import { Box, Flex, Grid } from '@chakra-ui/react';
import { BaseCard, createFieldHelper, HookFormInput } from '@invara-dev/react-base';
import { ReportingRequirementOptionInput } from './reportingRequirementOptionInput';
import {
  ActivityCompanyReportingRequirementViewModel,
  UpdateActivityCompanyReportingRequirementModel,
  UpdateActivityReportingRequirementsModel
} from '../../../../../features/activities/editActivity/editReportingRequirements/types';
import { ReportingRequirementViewModelDisplay } from '../../../../../features/activities/editActivity/editReportingRequirements/components/reportingRequirementViewModelDisplay';
import { ReportingValuePatternFailureExamples } from '../../../../../features/activities/editActivity/editReportingRequirements/components/reportingValuePatternFailureExamples';
import {
  getEffectiveReportingValueValidationPattern,
  getReportingValueRegexError
} from '../../../../../features/company/reportingRequirements/utils/reportingValueValidation';

type Props = {
  fieldArray: FieldArrayWithId<UpdateActivityReportingRequirementsModel, 'CompanyReportingRequirements', 'id'>[];
  viewModels: ActivityCompanyReportingRequirementViewModel[];
  formMethods: UseFormReturn<UpdateActivityReportingRequirementsModel>;
  isDisabled: boolean;
  hasBookedHotel: boolean;
  /** Number of columns used to lay out the requirement fields. Defaults to 1 (legacy stacked rows). */
  columns?: 1 | 2;
};

export const CompanyReportingRequirementsDisplay = (props: Props) => {
  const companyFieldHelper = createFieldHelper<UpdateActivityCompanyReportingRequirementModel>(props.formMethods, {
    inputProps: {
      disabled: props.isDisabled
    }
  });

  const getInputDef = (model: ActivityCompanyReportingRequirementViewModel) => {
    const pattern = getEffectiveReportingValueValidationPattern(model);
    return companyFieldHelper.textInput('Value').withValidation({
      required:
        props.hasBookedHotel && model.IsMandatory
          ? { message: 'Mandatory reporting requirements are required with a booked hotel.' }
          : undefined,
      validate: (v: string | undefined) => getReportingValueRegexError(v, pattern ?? null)
    });
  };

  const onOptionChange = (values: Array<string | undefined>, index: number) => {
    const newValue = values.join('-');
    props.formMethods.setValue(`CompanyReportingRequirements.${index}.Value`, newValue);
  };

  if (props.fieldArray.length == 0) {
    return <></>;
  }

  const columns = props.columns ?? 1;

  const renderField = (_field: (typeof props.fieldArray)[number], fieldIndex: number) => {
    const viewModel = props.viewModels.find((val) => val.ReportingRequirementId == _field.ReportingRequirementId);
    if (!viewModel) {
      return <Box key={fieldIndex}>Unable to find matching view model! Contact support.</Box>;
    }
    const fieldArrayKey = `CompanyReportingRequirements.${fieldIndex}.Value`;

    return (
      <Box key={fieldIndex} minWidth={columns === 2 ? undefined : '49%'} backgroundColor={'#e4e4e4'} padding={'4px'}>
        <ReportingRequirementViewModelDisplay viewModel={viewModel} />
        <Flex flexDir={'column'} gap={'4px'}>
          {viewModel.Options && viewModel.Options.length > 0 && (
            <ReportingRequirementOptionInput
              formMethods={props.formMethods}
              options={viewModel.Options}
              isDisabled={props.isDisabled}
              fieldArrayIndex={fieldIndex}
              fieldArrayPath={'TravellerReportingRequirements'}
              onChange={(val) => onOptionChange(val, fieldIndex)}
            />
          )}
          <Box>
            <HookFormInput inputDef={getInputDef(viewModel)} fieldArrayName={fieldArrayKey} />
            <ReportingValuePatternFailureExamples
              control={props.formMethods.control}
              valueName={fieldArrayKey}
              viewModel={viewModel}
            />
          </Box>
        </Flex>
      </Box>
    );
  };

  return (
    <BaseCard header={'Company Reporting Requirements'} variant={'fieldArray'} minW={'100%'} flex={1}>
      {columns === 2 ? (
        <Grid templateColumns='repeat(2, minmax(0, 1fr))' gap={'8px'} width={'100%'}>
          {props.fieldArray.map(renderField)}
        </Grid>
      ) : (
        <Flex flexDir={'column'} width={'100%'} gap={'4px'}>
          {props.fieldArray.map(renderField)}
        </Flex>
      )}
    </BaseCard>
  );
};
